import {
    ApiSpinTogetherInviteReqSchema,
    ApiSpinTogetherInviteResSchema,
    ApiSpinTogetherInviteRespondReqSchema,
    ApiSpinTogetherInviteRespondResSchema,
    ApiSpinTogetherPreferencesReqSchema,
    ApiSpinTogetherPreferencesResSchema,
    type ApiSpinTogetherInvite,
    type ApiSpinTogetherLiveActivityState,
    type ApiSpinTogetherPreference,
    type ApiSpinTogetherPresenceMode,
    type SpinTogetherRealtimeEventAny,
} from "@bridge-applications/pb-types";
import { useSlice } from "@bridge-applications/provider-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { authenticatedApiCall } from "../helpers/api";
import {
    SPIN_TOGETHER_DEFAULT_MAXIMUM_OUTCOME_AGE_SECONDS,
    preserveNewerSpinTogetherFriendState,
    recordSpinTogetherOutcomePresentation,
    spinTogetherActivityIsAtLeastAsRecent,
    spinTogetherLiveActivityIsAtLeastAsRecent,
    spinTogetherOutcomeIsAtLeastAsRecent,
    type SpinTogetherOutcomePresentation,
} from "../helpers/spinTogetherActivity";
import {
    SPIN_TOGETHER_TRANSPORT_V1,
    SPIN_TOGETHER_TRANSPORT_V2,
    reconcileSpinTogetherSnapshotRealtimeState,
    reconcileSpinTogetherSnapshotSequence,
    spinTogetherTransportRequiresReconnect,
    spinTogetherTransportSupportsPeriodicV2Refresh,
    spinTogetherV1SnapshotRequiresV2Retry,
    spinTogetherV2SnapshotRequiresV1Fallback,
    type SpinTogetherNormalizedSnapshot,
    type SpinTogetherTransport,
} from "../helpers/spinTogetherTransport";
import i18n from "../i18n/i18n";
import { useRequestSlotMachineRefetch } from "./useSlotMachine";

const MAX_RECENT_REWARDS = 20;
const RECONNECT_MAX_MS = 30_000;
const V2_PERIODIC_REFRESH_INTERVAL_MS = 60_000;

class SpinTogetherApiError extends Error {
    constructor(operation: string, code: string) {
        super(
            i18n.t("spinTogether.error", {
                operation,
                code,
                defaultValue: "Spin Together is temporarily unavailable.",
            }),
        );
        this.name = "SpinTogetherApiError";
    }
}

function sequenceAfter(current: string, incoming: string): boolean {
    return BigInt(incoming) > BigInt(current);
}

function sequenceIsNext(current: string, incoming: string): boolean {
    return BigInt(incoming) === BigInt(current) + 1n;
}

export function useSpinTogether(enabled: boolean) {
    const { state, setSlice } = useSlice("spinTogetherSlice");
    const [outcomePresentations, setOutcomePresentations] = useState<
        SpinTogetherOutcomePresentation[]
    >([]);
    const requestSlotMachineRefetch = useRequestSlotMachineRefetch();
    const mountedRef = useRef(false);
    const socketRef = useRef<WebSocket | null>(null);
    const snapshotRequestRef = useRef<AbortController | null>(null);
    const reconnectTimerRef = useRef<number | null>(null);
    const heartbeatTimerRef = useRef<number | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const sequenceRef = useRef("0");
    const transportRef = useRef<SpinTogetherTransport | null>(null);
    const configRef = useRef<SpinTogetherNormalizedSnapshot["config"]>(null);
    const connectRef = useRef<(() => Promise<void>) | null>(null);
    const connectRevisionRef = useRef(0);
    const realtimeAllowedRef = useRef(false);
    const outcomeMaximumAgeSecondsRef = useRef(
        SPIN_TOGETHER_DEFAULT_MAXIMUM_OUTCOME_AGE_SECONDS,
    );
    const reportedActivityRef =
        useRef<ApiSpinTogetherLiveActivityState>("IDLE");

    const refreshSnapshot = useCallback(
        async (backgroundV2Refresh: boolean) => {
            if (!enabled) return;
            if (backgroundV2Refresh) {
                if (
                    !spinTogetherTransportSupportsPeriodicV2Refresh(
                        transportRef.current,
                    ) ||
                    snapshotRequestRef.current !== null ||
                    document.visibilityState === "hidden"
                ) {
                    return;
                }
            } else {
                snapshotRequestRef.current?.abort();
            }
            const controller = new AbortController();
            snapshotRequestRef.current = controller;
            if (!backgroundV2Refresh) {
                setSlice({ loadStatus: "loading", error: null });
            }
            try {
                let snapshot: SpinTogetherNormalizedSnapshot;
                let nextTransport: SpinTogetherTransport;
                const selectedTransport = transportRef.current;
                const requestStartSequence = sequenceRef.current;
                if (backgroundV2Refresh) {
                    const { body: v2Body } = await authenticatedApiCall(
                        SPIN_TOGETHER_TRANSPORT_V2.snapshotPath(),
                        SPIN_TOGETHER_TRANSPORT_V2.snapshotResponseSchema,
                        { signal: controller.signal },
                    );
                    if (spinTogetherV2SnapshotRequiresV1Fallback(v2Body)) {
                        return;
                    }
                    if (v2Body.status === "ERROR") {
                        throw new SpinTogetherApiError("snapshot", v2Body.code);
                    }
                    nextTransport = SPIN_TOGETHER_TRANSPORT_V2;
                    snapshot =
                        SPIN_TOGETHER_TRANSPORT_V2.normalizeSnapshot(v2Body);
                } else if (selectedTransport?.version === 1) {
                    const { body: v1Body } = await authenticatedApiCall(
                        selectedTransport.snapshotPath(),
                        selectedTransport.snapshotResponseSchema,
                        { signal: controller.signal },
                    );
                    if (spinTogetherV1SnapshotRequiresV2Retry(v1Body)) {
                        const { body: v2Body } = await authenticatedApiCall(
                            SPIN_TOGETHER_TRANSPORT_V2.snapshotPath(),
                            SPIN_TOGETHER_TRANSPORT_V2.snapshotResponseSchema,
                            { signal: controller.signal },
                        );
                        if (v2Body.status === "ERROR") {
                            throw new SpinTogetherApiError(
                                "snapshot",
                                v2Body.code,
                            );
                        }
                        nextTransport = SPIN_TOGETHER_TRANSPORT_V2;
                        snapshot =
                            SPIN_TOGETHER_TRANSPORT_V2.normalizeSnapshot(
                                v2Body,
                            );
                    } else {
                        if (v1Body.status === "ERROR") {
                            throw new SpinTogetherApiError(
                                "snapshot",
                                v1Body.code,
                            );
                        }
                        nextTransport = SPIN_TOGETHER_TRANSPORT_V1;
                        snapshot =
                            SPIN_TOGETHER_TRANSPORT_V1.normalizeSnapshot(
                                v1Body,
                            );
                    }
                } else {
                    const { body: v2Body } = await authenticatedApiCall(
                        SPIN_TOGETHER_TRANSPORT_V2.snapshotPath(),
                        SPIN_TOGETHER_TRANSPORT_V2.snapshotResponseSchema,
                        { signal: controller.signal },
                    );
                    if (
                        spinTogetherV2SnapshotRequiresV1Fallback(v2Body) &&
                        selectedTransport === null
                    ) {
                        const { body: v1Body } = await authenticatedApiCall(
                            SPIN_TOGETHER_TRANSPORT_V1.snapshotPath(),
                            SPIN_TOGETHER_TRANSPORT_V1.snapshotResponseSchema,
                            { signal: controller.signal },
                        );
                        if (v1Body.status === "ERROR") {
                            throw new SpinTogetherApiError(
                                "snapshot",
                                v1Body.code,
                            );
                        }
                        nextTransport = SPIN_TOGETHER_TRANSPORT_V1;
                        snapshot =
                            SPIN_TOGETHER_TRANSPORT_V1.normalizeSnapshot(
                                v1Body,
                            );
                    } else {
                        if (v2Body.status === "ERROR") {
                            throw new SpinTogetherApiError(
                                "snapshot",
                                v2Body.code,
                            );
                        }
                        nextTransport = SPIN_TOGETHER_TRANSPORT_V2;
                        snapshot =
                            SPIN_TOGETHER_TRANSPORT_V2.normalizeSnapshot(
                                v2Body,
                            );
                    }
                }
                if (
                    !mountedRef.current ||
                    snapshotRequestRef.current !== controller ||
                    (backgroundV2Refresh &&
                        document.visibilityState === "hidden")
                ) {
                    return;
                }
                const sameRealtimeDomain =
                    transportRef.current !== null &&
                    transportRef.current.version === nextTransport.version;
                const sequenceReconciliation =
                    reconcileSpinTogetherSnapshotSequence(
                        requestStartSequence,
                        sequenceRef.current,
                        snapshot.lastSequence,
                        sameRealtimeDomain,
                    );
                const reconnectForTransportChange =
                    spinTogetherTransportRequiresReconnect(
                        transportRef.current,
                        nextTransport,
                    );
                transportRef.current = nextTransport;
                if (reconnectForTransportChange) {
                    connectRevisionRef.current += 1;
                    reconnectAttemptsRef.current = 0;
                    if (reconnectTimerRef.current !== null) {
                        window.clearTimeout(reconnectTimerRef.current);
                        reconnectTimerRef.current = null;
                    }
                    if (heartbeatTimerRef.current !== null) {
                        window.clearInterval(heartbeatTimerRef.current);
                        heartbeatTimerRef.current = null;
                    }
                    const socket = socketRef.current;
                    socketRef.current = null;
                    socket?.close(1000, "Realtime transport changed");
                }
                sequenceRef.current = sequenceReconciliation.committedSequence;
                const receivedAtMs = Date.now();
                if (!sequenceReconciliation.preserveCurrentRealtimeState) {
                    configRef.current = snapshot.config;
                    const maximumAgeSeconds =
                        snapshot.config?.outcomePresentation
                            ?.maximumAgeSeconds ??
                        SPIN_TOGETHER_DEFAULT_MAXIMUM_OUTCOME_AGE_SECONDS;
                    setOutcomePresentations((current) => {
                        let next = current;
                        for (const friend of snapshot.friends) {
                            if (!friend.latestOutcome) continue;
                            next = recordSpinTogetherOutcomePresentation(
                                next,
                                friend.user.userId,
                                friend.latestOutcome,
                                receivedAtMs,
                                maximumAgeSeconds,
                            );
                        }
                        return next;
                    });
                }
                outcomeMaximumAgeSecondsRef.current =
                    configRef.current?.outcomePresentation?.maximumAgeSeconds ??
                    SPIN_TOGETHER_DEFAULT_MAXIMUM_OUTCOME_AGE_SECONDS;
                realtimeAllowedRef.current =
                    configRef.current !== null &&
                    snapshot.preference.effectiveParticipationEligible &&
                    snapshot.preference.presenceMode !== "APPEAR_OFFLINE";
                setSlice((current) => {
                    const currentFriends = new Map(
                        current.friends.map((friend) => [
                            friend.user.userId,
                            friend,
                        ]),
                    );
                    const realtimeState =
                        reconcileSpinTogetherSnapshotRealtimeState(
                            {
                                config: current.config,
                                friends: current.friends,
                                invites: current.invites,
                                observedAt: current.observedAt,
                            },
                            {
                                config: snapshot.config,
                                friends: snapshot.friends.map((friend) =>
                                    preserveNewerSpinTogetherFriendState(
                                        currentFriends.get(friend.user.userId),
                                        friend,
                                    ),
                                ),
                                invites: snapshot.invites,
                                observedAt: snapshot.observedAt,
                            },
                            sequenceReconciliation.preserveCurrentRealtimeState,
                        );
                    return {
                        preference: snapshot.preference,
                        ...realtimeState,
                        loadStatus: "ready",
                        error: null,
                        lastSequence: sequenceReconciliation.committedSequence,
                    };
                });
                if (reconnectForTransportChange) {
                    queueMicrotask(() => {
                        if (
                            mountedRef.current &&
                            transportRef.current === nextTransport
                        ) {
                            void connectRef.current?.();
                        }
                    });
                }
            } catch (error) {
                if (controller.signal.aborted || !mountedRef.current) return;
                if (backgroundV2Refresh) return;
                setSlice({
                    loadStatus: "error",
                    error:
                        error instanceof Error ? error.message : String(error),
                });
            } finally {
                if (snapshotRequestRef.current === controller) {
                    snapshotRequestRef.current = null;
                }
            }
        },
        [enabled, setSlice],
    );

    const refresh = useCallback(
        () => refreshSnapshot(false),
        [refreshSnapshot],
    );
    const refreshV2InBackground = useCallback(
        () => refreshSnapshot(true),
        [refreshSnapshot],
    );

    const applyEvent = useCallback(
        (event: SpinTogetherRealtimeEventAny) => {
            if (!sequenceAfter(sequenceRef.current, event.sequence)) return;
            if (!sequenceIsNext(sequenceRef.current, event.sequence)) {
                void refresh();
                return;
            }
            sequenceRef.current = event.sequence;
            const receivedAtMs = Date.now();
            if (event.type === "outcome.settled") {
                setOutcomePresentations((current) =>
                    recordSpinTogetherOutcomePresentation(
                        current,
                        event.payload.userId,
                        event.payload.outcome,
                        receivedAtMs,
                        outcomeMaximumAgeSecondsRef.current,
                    ),
                );
            } else if (event.type === "friend.removed") {
                setOutcomePresentations((current) =>
                    current.filter(
                        (presentation) =>
                            presentation.friendUserId !==
                            event.payload.friendUserId,
                    ),
                );
            } else if (event.type === "config.changed") {
                configRef.current = event.payload.config;
                outcomeMaximumAgeSecondsRef.current =
                    event.payload.config?.outcomePresentation
                        ?.maximumAgeSeconds ??
                    SPIN_TOGETHER_DEFAULT_MAXIMUM_OUTCOME_AGE_SECONDS;
                if (event.payload.config === null) {
                    realtimeAllowedRef.current = false;
                }
            }
            setSlice((current) => {
                const common = {
                    lastSequence: event.sequence,
                    observedAt: event.occurredAt,
                };
                switch (event.type) {
                    case "presence.changed":
                        return {
                            ...common,
                            friends: current.friends.map((friend) =>
                                friend.user.userId === event.payload.userId
                                    ? {
                                          ...friend,
                                          presence: event.payload.presence,
                                          ...(event.payload.presence ===
                                          "OFFLINE"
                                              ? {
                                                    liveActivity: null,
                                                    activity:
                                                        friend.activity
                                                            ?.state ===
                                                            "SPINNING" ||
                                                        friend.activity
                                                            ?.state === "IDLE"
                                                            ? null
                                                            : friend.activity,
                                                }
                                              : {}),
                                      }
                                    : friend,
                            ),
                        };
                    case "activity.changed":
                        return {
                            ...common,
                            friends: current.friends.map((friend) => {
                                if (
                                    friend.user.userId !== event.payload.userId
                                ) {
                                    return friend;
                                }
                                if (
                                    !spinTogetherActivityIsAtLeastAsRecent(
                                        friend.activity,
                                        event.payload.activity,
                                    )
                                ) {
                                    return friend;
                                }
                                return {
                                    ...friend,
                                    activity: event.payload.activity,
                                };
                            }),
                        };
                    case "activity.live.changed":
                        return {
                            ...common,
                            friends: current.friends.map((friend) => {
                                if (
                                    friend.user.userId !== event.payload.userId
                                ) {
                                    return friend;
                                }
                                if (
                                    !spinTogetherLiveActivityIsAtLeastAsRecent(
                                        friend.liveActivity,
                                        event.payload.activity,
                                    )
                                ) {
                                    return friend;
                                }
                                return {
                                    ...friend,
                                    liveActivity: event.payload.activity,
                                };
                            }),
                        };
                    case "outcome.settled":
                        return {
                            ...common,
                            friends: current.friends.map((friend) => {
                                if (
                                    friend.user.userId !== event.payload.userId
                                ) {
                                    return friend;
                                }
                                if (
                                    !spinTogetherOutcomeIsAtLeastAsRecent(
                                        friend.latestOutcome,
                                        event.payload.outcome,
                                    )
                                ) {
                                    return friend;
                                }
                                return {
                                    ...friend,
                                    latestOutcome: event.payload.outcome,
                                };
                            }),
                        };
                    case "pair.started":
                        return {
                            ...common,
                            friends: current.friends.map((friend) =>
                                friend.user.userId ===
                                event.payload.friendUserId
                                    ? {
                                          ...friend,
                                          session: event.payload.session,
                                      }
                                    : friend,
                            ),
                        };
                    case "pair.ended":
                        return {
                            ...common,
                            friends: current.friends.map((friend) =>
                                friend.user.userId ===
                                    event.payload.friendUserId &&
                                friend.session?.sessionId ===
                                    event.payload.sessionId
                                    ? { ...friend, session: null }
                                    : friend,
                            ),
                        };
                    case "reward.granted":
                        return {
                            ...common,
                            recentRewards: [
                                event.payload,
                                ...current.recentRewards.filter(
                                    (reward) =>
                                        reward.grantId !==
                                        event.payload.grantId,
                                ),
                            ].slice(0, MAX_RECENT_REWARDS),
                        };
                    case "invite.changed":
                        return {
                            ...common,
                            invites: [
                                event.payload,
                                ...current.invites.filter(
                                    (invite) =>
                                        invite.inviteId !==
                                        event.payload.inviteId,
                                ),
                            ],
                        };
                    case "friend.changed": {
                        const currentFriend = current.friends.find(
                            (friend) =>
                                friend.user.userId ===
                                event.payload.user.userId,
                        );
                        return {
                            ...common,
                            friends: [
                                preserveNewerSpinTogetherFriendState(
                                    currentFriend,
                                    event.payload,
                                ),
                                ...current.friends.filter(
                                    (friend) =>
                                        friend.user.userId !==
                                        event.payload.user.userId,
                                ),
                            ],
                        };
                    }
                    case "friend.removed":
                        return {
                            ...common,
                            friends: current.friends.filter(
                                (friend) =>
                                    friend.user.userId !==
                                    event.payload.friendUserId,
                            ),
                        };
                    case "config.changed":
                        return { ...common, config: event.payload.config };
                    case "snapshot.required":
                        return common;
                }
            });
            if (event.type === "reward.granted") {
                requestSlotMachineRefetch();
            } else if (event.type === "snapshot.required") {
                void refresh();
            }
        },
        [refresh, requestSlotMachineRefetch, setSlice],
    );

    const pollMissedEvents = useCallback(async () => {
        if (!enabled) return;
        const transport = transportRef.current;
        if (!transport) return;
        setSlice({ realtimeStatus: "polling" });
        if (transport.version === 2) {
            const { body } = await authenticatedApiCall(
                transport.eventsPath(sequenceRef.current),
                transport.eventsResponseSchema,
            );
            if (transportRef.current !== transport) return;
            if (body.status === "ERROR") {
                if (body.code === "CURSOR_EXPIRED") {
                    await refresh();
                    return;
                }
                throw new SpinTogetherApiError("events", body.code);
            }
            for (const event of body.events) applyEvent(event);
            return;
        }
        const { body } = await authenticatedApiCall(
            transport.eventsPath(sequenceRef.current),
            transport.eventsResponseSchema,
        );
        if (transportRef.current !== transport) return;
        if (body.status === "ERROR") {
            if (body.code === "CURSOR_EXPIRED") {
                await refresh();
                return;
            }
            throw new SpinTogetherApiError("events", body.code);
        }
        for (const event of body.events) applyEvent(event);
    }, [applyEvent, enabled, refresh, setSlice]);

    const connect = useCallback(async () => {
        if (!enabled || !mountedRef.current) return;
        if (!realtimeAllowedRef.current) {
            setSlice({ realtimeStatus: "disabled" });
            return;
        }
        if (reconnectTimerRef.current !== null) {
            window.clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
        const revision = ++connectRevisionRef.current;
        const previousSocket = socketRef.current;
        socketRef.current = null;
        previousSocket?.close(1000, "Replacing connection");
        setSlice({ realtimeStatus: "connecting" });
        try {
            await pollMissedEvents();
            const transport = transportRef.current;
            if (!transport) return;
            let ticket: {
                url: string;
                ticket: string;
                heartbeatSeconds: number;
            };
            if (transport.version === 2) {
                const request = transport.realtimeTicketRequest(
                    sequenceRef.current,
                );
                const { body } = await authenticatedApiCall(
                    transport.realtimeTicketPath,
                    transport.realtimeTicketResponseSchema,
                    { method: "POST", body: JSON.stringify(request) },
                );
                if (body.status === "ERROR") {
                    throw new SpinTogetherApiError("realtime", body.code);
                }
                ticket = body;
            } else {
                const request = transport.realtimeTicketRequest(
                    sequenceRef.current,
                );
                const { body } = await authenticatedApiCall(
                    transport.realtimeTicketPath,
                    transport.realtimeTicketResponseSchema,
                    { method: "POST", body: JSON.stringify(request) },
                );
                if (body.status === "ERROR") {
                    throw new SpinTogetherApiError("realtime", body.code);
                }
                ticket = body;
            }
            if (
                !mountedRef.current ||
                connectRevisionRef.current !== revision
            ) {
                return;
            }
            const url = new URL(ticket.url);
            url.searchParams.set("ticket", ticket.ticket);
            const socket = new WebSocket(url);
            socketRef.current = socket;
            socket.addEventListener("open", () => {
                if (socketRef.current !== socket) return;
                setSlice({ realtimeStatus: "connected" });
                socket.send(
                    JSON.stringify({
                        type: "activity.state",
                        state: reportedActivityRef.current,
                        activityKind: "SM_SPIN_SETTLED",
                    }),
                );
                if (heartbeatTimerRef.current !== null) {
                    window.clearInterval(heartbeatTimerRef.current);
                }
                heartbeatTimerRef.current = window.setInterval(() => {
                    if (socket.readyState === WebSocket.OPEN) {
                        socket.send(JSON.stringify({ type: "ping" }));
                    }
                }, ticket.heartbeatSeconds * 1_000);
            });
            socket.addEventListener("message", (message) => {
                if (socketRef.current !== socket) return;
                if (typeof message.data !== "string") return;
                let raw: unknown;
                try {
                    raw = JSON.parse(message.data);
                } catch {
                    return;
                }
                if (
                    typeof raw === "object" &&
                    raw !== null &&
                    "type" in raw &&
                    raw.type === "pong"
                ) {
                    reconnectAttemptsRef.current = 0;
                    return;
                }
                const parsed = transport.realtimeEventSchema.safeParse(raw);
                if (parsed.success) {
                    reconnectAttemptsRef.current = 0;
                    applyEvent(parsed.data);
                } else {
                    socket.close(1002, "Invalid realtime event");
                }
            });
            socket.addEventListener("close", () => {
                if (
                    socketRef.current !== socket ||
                    !mountedRef.current ||
                    !enabled
                ) {
                    return;
                }
                socketRef.current = null;
                if (heartbeatTimerRef.current !== null) {
                    window.clearInterval(heartbeatTimerRef.current);
                    heartbeatTimerRef.current = null;
                }
                if (!realtimeAllowedRef.current) {
                    setSlice({ realtimeStatus: "disabled" });
                    return;
                }
                if (document.visibilityState === "hidden") {
                    setSlice({ realtimeStatus: "disabled" });
                    return;
                }
                setSlice({ realtimeStatus: "polling" });
                const delay = Math.min(
                    RECONNECT_MAX_MS,
                    1_000 * 2 ** reconnectAttemptsRef.current++,
                );
                reconnectTimerRef.current = window.setTimeout(() => {
                    reconnectTimerRef.current = null;
                    void connect();
                }, delay);
            });
        } catch (error) {
            if (
                !mountedRef.current ||
                connectRevisionRef.current !== revision
            ) {
                return;
            }
            setSlice({
                realtimeStatus: "polling",
                error: error instanceof Error ? error.message : String(error),
            });
            const delay = Math.min(
                RECONNECT_MAX_MS,
                1_000 * 2 ** reconnectAttemptsRef.current++,
            );
            reconnectTimerRef.current = window.setTimeout(() => {
                reconnectTimerRef.current = null;
                void connect();
            }, delay);
        }
    }, [applyEvent, enabled, pollMissedEvents, setSlice]);

    useEffect(() => {
        connectRef.current = connect;
        return () => {
            if (connectRef.current === connect) {
                connectRef.current = null;
            }
        };
    }, [connect]);

    useEffect(() => {
        if (enabled) return;
        configRef.current = null;
        sequenceRef.current = "0";
        transportRef.current = null;
        realtimeAllowedRef.current = false;
        setOutcomePresentations([]);
        setSlice({
            preference: null,
            config: null,
            friends: [],
            invites: [],
            recentRewards: [],
            loadStatus: "idle",
            realtimeStatus: "disabled",
            mutationStatus: "idle",
            error: null,
            observedAt: null,
            lastSequence: "0",
        });
    }, [enabled, setSlice]);

    useEffect(() => {
        if (!enabled) return;
        const timer = window.setInterval(() => {
            void refreshV2InBackground();
        }, V2_PERIODIC_REFRESH_INTERVAL_MS);
        return () => window.clearInterval(timer);
    }, [enabled, refreshV2InBackground]);

    useEffect(() => {
        mountedRef.current = true;
        if (enabled) {
            void refresh().then(() => {
                if (mountedRef.current) void connect();
            });
        }
        return () => {
            mountedRef.current = false;
            connectRevisionRef.current += 1;
            snapshotRequestRef.current?.abort();
            if (reconnectTimerRef.current !== null) {
                window.clearTimeout(reconnectTimerRef.current);
            }
            if (heartbeatTimerRef.current !== null) {
                window.clearInterval(heartbeatTimerRef.current);
            }
            const socket = socketRef.current;
            socketRef.current = null;
            socket?.close(1000, "Component unmounted");
        };
    }, [connect, enabled, refresh]);

    useEffect(() => {
        if (!enabled) return;
        const handleVisibility = () => {
            if (document.visibilityState === "hidden") {
                if (reconnectTimerRef.current !== null) {
                    window.clearTimeout(reconnectTimerRef.current);
                    reconnectTimerRef.current = null;
                }
                const socket = socketRef.current;
                socketRef.current = null;
                socket?.close(1000, "App hidden");
                setSlice({ realtimeStatus: "disabled" });
                return;
            }
            void refresh().then(() => void connect());
        };
        document.addEventListener("visibilitychange", handleVisibility);
        return () =>
            document.removeEventListener("visibilitychange", handleVisibility);
    }, [connect, enabled, refresh]);

    const updatePreference = useCallback(
        async (
            patch: Partial<
                Pick<
                    ApiSpinTogetherPreference,
                    | "presenceMode"
                    | "participationEnabled"
                    | "shareExactWinnings"
                    | "allowInvites"
                >
            >,
        ) => {
            if (!state.preference) return;
            setSlice({ mutationStatus: "saving", error: null });
            try {
                const request = ApiSpinTogetherPreferencesReqSchema.parse({
                    presenceMode:
                        patch.presenceMode ?? state.preference.presenceMode,
                    participationEnabled:
                        patch.participationEnabled ??
                        state.preference.participationEnabled,
                    shareExactWinnings:
                        patch.shareExactWinnings ??
                        state.preference.shareExactWinnings,
                    allowInvites:
                        patch.allowInvites ?? state.preference.allowInvites,
                    expectedVersion: state.preference.version,
                });
                const { body } = await authenticatedApiCall(
                    "/api/v1/spin-together/preferences",
                    ApiSpinTogetherPreferencesResSchema,
                    { method: "PUT", body: JSON.stringify(request) },
                );
                if (body.status === "ERROR") {
                    if (body.code === "VERSION_CONFLICT") await refresh();
                    throw new SpinTogetherApiError("preferences", body.code);
                }
                setSlice({
                    preference: body.preference,
                    mutationStatus: "idle",
                });
                realtimeAllowedRef.current =
                    configRef.current !== null &&
                    body.preference.effectiveParticipationEligible &&
                    body.preference.presenceMode !== "APPEAR_OFFLINE";
                if (!realtimeAllowedRef.current) {
                    const socket = socketRef.current;
                    socketRef.current = null;
                    socket?.close(1000, "Presence disabled");
                    setSlice({ realtimeStatus: "disabled" });
                } else if (!socketRef.current) {
                    void connect();
                }
            } catch (error) {
                setSlice({
                    mutationStatus: "error",
                    error:
                        error instanceof Error ? error.message : String(error),
                });
                throw error;
            }
        },
        [connect, refresh, setSlice, state.preference],
    );

    const invite = useCallback(
        async (friendUserId: string): Promise<ApiSpinTogetherInvite> => {
            const request = ApiSpinTogetherInviteReqSchema.parse({
                friendUserId,
                idempotencyKey: crypto.randomUUID(),
            });
            const { body } = await authenticatedApiCall(
                "/api/v1/spin-together/invites",
                ApiSpinTogetherInviteResSchema,
                { method: "POST", body: JSON.stringify(request) },
            );
            if (body.status === "ERROR") {
                throw new SpinTogetherApiError("invite", body.code);
            }
            setSlice((current) => ({
                invites: [
                    body.invite,
                    ...current.invites.filter(
                        (item) => item.inviteId !== body.invite.inviteId,
                    ),
                ],
            }));
            return body.invite;
        },
        [setSlice],
    );

    const respondToInvite = useCallback(
        async (inviteId: string, response: "ACCEPT" | "DECLINE") => {
            const request = ApiSpinTogetherInviteRespondReqSchema.parse({
                inviteId,
                response,
            });
            const { body } = await authenticatedApiCall(
                "/api/v1/spin-together/invites/respond",
                ApiSpinTogetherInviteRespondResSchema,
                { method: "POST", body: JSON.stringify(request) },
            );
            if (body.status === "ERROR") {
                throw new SpinTogetherApiError("invite", body.code);
            }
            setSlice((current) => ({
                invites: current.invites.map((item) =>
                    item.inviteId === inviteId ? body.invite : item,
                ),
            }));
            return body.invite;
        },
        [setSlice],
    );

    const reportActivity = useCallback(
        (stateValue: ApiSpinTogetherLiveActivityState) => {
            reportedActivityRef.current = stateValue;
            if (socketRef.current?.readyState !== WebSocket.OPEN) return;
            socketRef.current.send(
                JSON.stringify({
                    type: "activity.state",
                    state: stateValue,
                    activityKind: "SM_SPIN_SETTLED",
                }),
            );
        },
        [],
    );

    return {
        ...state,
        enabled,
        outcomePresentations,
        refresh,
        updatePreference,
        invite,
        respondToInvite,
        reportActivity,
    };
}

export type SpinTogetherController = ReturnType<typeof useSpinTogether>;
export type SpinTogetherPresenceMode = ApiSpinTogetherPresenceMode;
